import MainLayout from "@/components/layout/MainLayout";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RecordCard } from "@/components/dashboard/RecordCard";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { ResponsiveFilterGroup } from "@/components/ui/responsive-filter-group";
import { EnhancedEmptyState, getEmptyStateType } from "@/components/ui/enhanced-empty-state";
import { useInfiniteRecords } from "@/hooks/useInfiniteRecords";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { LoadingTrigger } from "@/components/records/LoadingTrigger";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import RecordCardSkeleton from "@/components/dashboard/RecordCardSkeleton";
import { Button } from "@/components/ui/button";
import { Database } from "@/integrations/supabase/types";
import PullToRefresh from "@/components/ui/pull-to-refresh";
import { useTranslation } from "react-i18next";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];
type SortBy = "newest" | "oldest" | "name-asc" | "name-desc";

interface InfiniteScrollState {
  search: string;
  statusFilter: ProcessingStatus | "all";
  sortBy: SortBy;
  selectedTags: string[];
}

const Dashboard = () => {
  const { t } = useTranslation();
  // Infinite scroll state
  const [scrollState, setScrollState] = useState<InfiniteScrollState>({
    search: "",
    statusFilter: "all",
    sortBy: "newest",
    selectedTags: [],
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Проверяем, передан ли фильтр по тегу через состояние навигации
  useEffect(() => {
    if (location.state?.filterByTag) {
      setScrollState(prev => ({
        ...prev,
        selectedTags: [location.state.filterByTag],
      }));
      // Очищаем состояние навигации после применения фильтра
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  // Use infinite scroll - only if user is authenticated
  const {
    records,
    totalRecords,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteRecords({
    userId: user?.id || "",
    enabled: !!user?.id,
    limit: 20,
    ...scrollState,
  });


  // Debug logging (remove after fixing)
  console.log("Dashboard Debug:", {
    user: user?.id,
    enabled: !!user?.id,
    scrollState,
    isLoading,
    error,
    recordsCount: records.length,
    totalRecords,
    hasNextPage,
    isFetchingNextPage,
  });

  const handleNewUpload = () => navigate("/");

  // Show loading or auth message if user not ready
  if (!user) {
    return (
      <MainLayout>
        <div className="container-page">
          <div className="text-center py-8">
            <LoadingSpinner message={t('dashboard.auth_check')} />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!user.id) {
    return (
      <MainLayout>
        <div className="container-page">
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">
              {t('dashboard.auth_error_title')}
            </div>
            <Button onClick={() => window.location.reload()}>
              {t('dashboard.reload_btn')}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Filter change handlers - now trigger refetch and scroll to top
  const handleSearchChange = (searchTerm: string) => {
    setScrollState((prev) => ({
      ...prev,
      search: searchTerm,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusChange = (status: ProcessingStatus | "all") => {
    setScrollState((prev) => ({
      ...prev,
      statusFilter: status,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (sortBy: SortBy) => {
    setScrollState((prev) => ({
      ...prev,
      sortBy,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTagsChange = (tagIds: string[]) => {
    setScrollState((prev) => ({
      ...prev,
      selectedTags: tagIds,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset handlers for empty states
  const handleShowAll = () => {
    setScrollState({
      search: "",
      statusFilter: "all",
      sortBy: "newest",
      selectedTags: [],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSearch = () => {
    setScrollState((prev) => ({
      ...prev,
      search: "",
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setScrollState((prev) => ({
      ...prev,
      statusFilter: "all",
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleRefresh = async () => {
    try {
      console.log(`🔄 Pull-to-refresh: ${t('dashboard.pull_refresh.updating')}`);
      await refetch();
      // Скроллим к началу после обновления для лучшего UX
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      console.log(`✅ Pull-to-refresh: ${t('dashboard.pull_refresh.updated')}`);
    } catch (error) {
      console.error(`❌ Pull-to-refresh: ${t('dashboard.pull_refresh.error')}:`, error);
    }
  };

  return (
    <MainLayout>
      <PullToRefresh
        onRefresh={handleRefresh}
        disabled={isLoading}
        className="min-h-screen"
      >
        <div className="container-page">
          <DashboardHeader onNewUpload={handleNewUpload} />

          {/* Filter Section with proper heading hierarchy */}
          <section aria-labelledby="filters-heading" className="mb-6">
            <h2 id="filters-heading" className="sr-only">
              {t('dashboard.filters_heading')}
            </h2>
            <ResponsiveFilterGroup
              searchTerm={scrollState.search}
              statusFilter={scrollState.statusFilter}
              sortBy={scrollState.sortBy}
              selectedTagIds={scrollState.selectedTags}
              onSearchChange={handleSearchChange}
              onStatusChange={handleStatusChange}
              onSortChange={handleSortChange}
              onTagsChange={handleTagsChange}
              userId={user.id}
              showTagFilter={true}
              onResetAll={handleShowAll}
            />
          </section>

          {isLoading && records.length === 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {Array.from({ length: 8 }, (_, i) => (
                  <RecordCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-4">
                {t('dashboard.load_error')}{" "}
                {error instanceof Error ? error.message : t('dashboard.unknown_error')}
              </div>
              <Button onClick={() => window.location.reload()}>
                {t('dashboard.reload_btn')}
              </Button>
            </div>
          ) : records.length > 0 ? (
            <section aria-labelledby="records-heading">
              <h2 id="records-heading" className="sr-only">
                {t('dashboard.records_list_heading')} ({records.length} {t('common.out_of', { defaultValue: 'из' })} {totalRecords})
              </h2>
              <div
                className="space-y-6"
                role="region"
                aria-live="polite"
                aria-label={t('dashboard.records_count_aria', { count: records.length, total: totalRecords })}
              >
                <div className="grid grid-cols-1 gap-4">
                  {records.map((record) => (
                    <RecordCard
                      key={record.id}
                      record={record}
                      isPublished={record.isPublished || false}
                      shareUrl={record.shareUrl}
                    />
                  ))}
                </div>

                <LoadingTrigger
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={handleLoadMore}
                  error={error}
                  onRetry={() => refetch()}
                />
              </div>
            </section>
          ) : (
            (() => {
              const emptyStateType = getEmptyStateType(
                totalRecords > 0,
                scrollState.search,
                scrollState.statusFilter
              );

              const commonProps = {
                onUpload: handleNewUpload,
              };

              switch (emptyStateType) {
                case "no-records":
                  return (
                    <EnhancedEmptyState
                      type="no-records"
                      {...commonProps}
                    />
                  );
                case "no-filter-results":
                  return (
                    <EnhancedEmptyState
                      type="no-filter-results"
                      statusFilter={scrollState.statusFilter as ProcessingStatus}
                      onShowAll={handleShowAll}
                      {...commonProps}
                    />
                  );
                case "no-search-results":
                  return (
                    <EnhancedEmptyState
                      type="no-search-results"
                      searchTerm={scrollState.search}
                      onClearSearch={handleClearSearch}
                      onShowAll={handleShowAll}
                      {...commonProps}
                    />
                  );
                case "combined-empty":
                  return (
                    <EnhancedEmptyState
                      type="combined-empty"
                      searchTerm={scrollState.search}
                      statusFilter={scrollState.statusFilter as ProcessingStatus}
                      onClearSearch={handleClearSearch}
                      onResetFilters={handleResetFilters}
                      onShowAll={handleShowAll}
                      {...commonProps}
                    />
                  );
                default:
                  return (
                    <EnhancedEmptyState
                      type="no-records"
                      {...commonProps}
                    />
                  );
              }
            })()
          )}
        </div>
      </PullToRefresh>
    </MainLayout>
  );
};

export default Dashboard;
